/**
 * A spoken note attached to what you just sent.
 *
 * "Dit is de factuur van de loodgieter, hij komt dinsdag terug om de rest te
 * doen." That sentence is worth more than any field on a form, and typing it
 * on a phone while standing in a kitchen is exactly the friction this app
 * exists to remove.
 *
 * Deliberately **not transcribed**. Transcription costs money or means sending
 * the family's voice to a service, and neither is needed for the thing to be
 * useful: the note is fifteen seconds long and the person who has to hear it
 * is the one who opens the document anyway. If transcription is ever added,
 * the recording stays the original — the same rule as documents.
 */

/**
 * iOS gives you audio/mp4 and refuses webm; desktop Chrome is the other way
 * round. Asking the browser what it can do beats guessing, and a wrong mime
 * type here produces a file nothing will play.
 */
export function pickAudioType(): string {
  const candidates = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
  for (const type of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }
  return ''
}

export function audioExtension(contentType: string): string {
  if (contentType.startsWith('audio/mp4')) return 'm4a'
  if (contentType.startsWith('audio/webm')) return 'webm'
  return 'bin'
}

export function isAudio(contentType: string | null | undefined): boolean {
  return Boolean(contentType?.startsWith('audio/'))
}

export interface Recorder {
  stop: () => Promise<Blob>
  cancel: () => void
}

/** Longer than this is a phone call, not a note. */
export const MAX_SECONDS = 90

export async function startRecording(): Promise<Recorder> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const mimeType = pickAudioType()
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
  const chunks: BlobPart[] = []

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data)
  }
  recorder.start()

  // The microphone light stays on until every track is stopped, and a light
  // that stays on after you think you stopped is its own kind of betrayal.
  const release = () => stream.getTracks().forEach((t) => t.stop())

  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          release()
          resolve(new Blob(chunks, { type: mimeType || 'audio/mp4' }))
        }
        recorder.stop()
      }),
    cancel: () => {
      try {
        recorder.stop()
      } catch {
        // Already stopped; the tracks still need releasing.
      }
      release()
    },
  }
}

export function formatSeconds(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
